import {
    IconVolume, IconVolume3, IconKeyboard, IconTypography, IconDeviceLaptop, IconBubble, IconPiano,
    IconAlertOctagon, IconBell, IconPalette, IconHandFinger, IconEye, IconTextSize, IconPlayerPlayFilled,
} from '@tabler/icons-react';
import { SOUND_PACKS, previewPack, playError } from '../engine/sound';

export const DEFAULT_PREFS = {
    sound: true,
    soundPack: 'mechanical',
    volume: 55,
    errorSound: true,
    finishSound: true,
    showKeyboard: true,
    fingerColors: true,
    dimKeys: true,
    fingerHint: true,
    textSize: 'md',
};

/** Font size of the practice text for each size choice — strict drill, free sheet, typed line. */
export const TEXT_SIZES = {
    sm: { strict: 26, sheet: 24, input: 22 },
    md: { strict: 32, sheet: 30, input: 26 },
    lg: { strict: 40, sheet: 36, input: 30 },
};

const PACK_ICON = { off: IconVolume3, mechanical: IconKeyboard, typewriter: IconTypography, laptop: IconDeviceLaptop, bubble: IconBubble, piano: IconPiano };

export function Switch({ on, onChange, label }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={label}
            onClick={() => onChange(!on)}
            className={`relative h-[22px] w-[38px] flex-none rounded-full transition-colors ${on ? 'bg-primary' : 'bg-[#DDD6CF] dark:bg-zinc-700'}`}
        >
            <span className={`absolute top-[3px] h-4 w-4 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-all ${on ? 'left-[19px]' : 'left-[3px]'}`} />
        </button>
    );
}

function Row({ icon: Icon, label, hint, children, disabled }) {
    return (
        <div className={`flex items-center justify-between gap-3 py-2 ${disabled ? 'pointer-events-none opacity-45' : ''}`}>
            <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#F4F1ED] text-[#7A726B] dark:bg-zinc-800 dark:text-zinc-400">
                    <Icon size={16} stroke={1.8} />
                </span>
                <div className="min-w-0">
                    <div className="text-[13px] font-semibold leading-tight text-[#2C2722] dark:text-zinc-200">{label}</div>
                    {hint && <div className="mt-0.5 text-[11.5px] leading-snug text-[#A39B93] dark:text-zinc-500">{hint}</div>}
                </div>
            </div>
            {children}
        </div>
    );
}

const SectionTitle = ({ children }) => (
    <div className="mb-1 mt-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[#B0A89F] dark:text-zinc-500">{children}</div>
);

/**
 * Sound and display preferences. `prefs` is the merged preference object;
 * `onChange(patch)` saves a partial update. Used on the page and inside the
 * practice window, so the same choices show in both.
 */
export default function SettingsPanel({ prefs, onChange, t, n }) {
    const soundOff = !prefs.sound;
    const pickPack = (pack) => {
        onChange({ soundPack: pack, sound: true });
        previewPack(pack, prefs.volume || DEFAULT_PREFS.volume);
    };

    return (
        <div className="flex flex-col">
            <SectionTitle>{t('typingPractice.set.soundTitle')}</SectionTitle>
            <div className="mb-2 mt-1">
                <div className="text-[13px] font-semibold leading-tight text-[#2C2722] dark:text-zinc-200">{t('typingPractice.set.keySound')}</div>
                <div className="mt-0.5 text-[11.5px] leading-snug text-[#A39B93] dark:text-zinc-500">{t('typingPractice.set.keySoundHint')}</div>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
                {['off', ...SOUND_PACKS].map((pack) => {
                    const Icon = PACK_ICON[pack];
                    const active = pack === 'off' ? soundOff : !soundOff && prefs.soundPack === pack;
                    return (
                        <button
                            key={pack}
                            type="button"
                            onClick={() => (pack === 'off' ? onChange({ sound: false }) : pickPack(pack))}
                            title={pack === 'off' ? undefined : t(`typingPractice.pack.${pack}Hint`)}
                            aria-pressed={active}
                            className={`flex flex-col items-center gap-1 rounded-xl border px-1 py-2.5 transition-all ${active
                                ? 'border-primary bg-primary/[0.07] text-primary shadow-[0_2px_8px_rgba(230,138,0,0.15)]'
                                : 'border-[#ECE8E4] text-[#7A726B] hover:-translate-y-px hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:text-zinc-400'}`}
                        >
                            <Icon size={19} stroke={1.7} />
                            <span className="text-center font-hind text-[11.5px] font-semibold leading-tight">{t(`typingPractice.pack.${pack}`)}</span>
                        </button>
                    );
                })}
            </div>

            <div className={`mt-2.5 flex items-center gap-3 rounded-xl bg-[#F8F6F3] px-3 py-2.5 dark:bg-zinc-800/60 ${soundOff ? 'pointer-events-none opacity-45' : ''}`}>
                <IconVolume3 size={15} className="flex-none text-[#A39B93]" />
                <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={prefs.volume}
                    onChange={(e) => onChange({ volume: Number(e.target.value) })}
                    aria-label={t('typingPractice.set.volume')}
                    className="h-1.5 flex-1 cursor-pointer accent-primary"
                />
                <IconVolume size={15} className="flex-none text-[#A39B93]" />
                <span className="w-9 flex-none text-right text-[12px] font-bold tabular-nums text-[#5A534C] dark:text-zinc-300">{n(prefs.volume)}%</span>
                <button
                    type="button"
                    onClick={() => previewPack(prefs.soundPack, prefs.volume)}
                    aria-label={t('typingPractice.set.listen')}
                    title={t('typingPractice.set.listen')}
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white text-primary shadow-[0_1px_2px_rgba(0,0,0,0.08)] transition-transform hover:scale-105 dark:bg-zinc-900"
                >
                    <IconPlayerPlayFilled size={13} />
                </button>
            </div>

            <Row icon={IconAlertOctagon} label={t('typingPractice.set.errorSound')} disabled={soundOff}>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => playError(prefs.volume)}
                        aria-label={t('typingPractice.set.listen')}
                        title={t('typingPractice.set.listen')}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-[#A39B93] transition-colors hover:bg-primary/10 hover:text-primary"
                    >
                        <IconPlayerPlayFilled size={12} />
                    </button>
                    <Switch on={prefs.errorSound} onChange={(v) => onChange({ errorSound: v })} label={t('typingPractice.set.errorSound')} />
                </div>
            </Row>
            <Row icon={IconBell} label={t('typingPractice.set.finishSound')} disabled={soundOff}>
                <Switch on={prefs.finishSound} onChange={(v) => onChange({ finishSound: v })} label={t('typingPractice.set.finishSound')} />
            </Row>

            <div className="my-2 h-px bg-[#EFEBE7] dark:bg-zinc-800" />
            <SectionTitle>{t('typingPractice.set.displayTitle')}</SectionTitle>

            <Row icon={IconKeyboard} label={t('typingPractice.set.keyboard')}>
                <Switch on={prefs.showKeyboard} onChange={(v) => onChange({ showKeyboard: v })} label={t('typingPractice.set.keyboard')} />
            </Row>
            <Row icon={IconPalette} label={t('typingPractice.set.colors')} disabled={!prefs.showKeyboard}>
                <Switch on={prefs.fingerColors} onChange={(v) => onChange({ fingerColors: v })} label={t('typingPractice.set.colors')} />
            </Row>
            <Row icon={IconEye} label={t('typingPractice.set.dim')} hint={t('typingPractice.set.dimHint')} disabled={!prefs.showKeyboard}>
                <Switch on={prefs.dimKeys} onChange={(v) => onChange({ dimKeys: v })} label={t('typingPractice.set.dim')} />
            </Row>
            <Row icon={IconHandFinger} label={t('typingPractice.set.fingerHint')}>
                <Switch on={prefs.fingerHint} onChange={(v) => onChange({ fingerHint: v })} label={t('typingPractice.set.fingerHint')} />
            </Row>
            <Row icon={IconTextSize} label={t('typingPractice.set.textSize')}>
                <div className="inline-flex flex-none rounded-lg bg-[#F4F1ED] p-0.5 dark:bg-zinc-800">
                    {Object.keys(TEXT_SIZES).map((size) => (
                        <button
                            key={size}
                            type="button"
                            onClick={() => onChange({ textSize: size })}
                            className={`rounded-md px-2.5 py-1 text-[12px] font-bold transition-all ${prefs.textSize === size
                                ? 'bg-white text-primary shadow-[0_1px_2px_rgba(28,24,21,0.12)] dark:bg-zinc-700'
                                : 'text-[#8A837C] hover:text-[#3D3833] dark:text-zinc-400'}`}
                        >
                            {t(`typingPractice.set.size.${size}`)}
                        </button>
                    ))}
                </div>
            </Row>
        </div>
    );
}
